import React, { useState, useEffect } from 'react';
import { Package2, ChevronRight, Palette } from 'lucide-react';
import ProductDetail from './ProductDetail';
import ShelfView from './ShelfView';
import ProductCatalog from './ProductCatalog';
import { AccountSettings } from './AccountSettings';
import { PersonalProfile } from './PersonalProfile';
import SkinProgressView from './SkinProgressView';
import SkinRoutine from './SkinRoutine';
import SkinTimeline from './SkinTimeline';
import SectionHeader from './SectionHeader';
import { trackRecentAnalysisOpened } from '../utils/analytics';
import CatalogProductImage from './CatalogProductImage';

// Brand editorial serif — matches the Welcome screen / onboarding.
const SERIF_FF = '"DM Sans",system-ui,sans-serif';
const SHOW_COLOUR_ANALYSIS = process.env.REACT_APP_ENABLE_COLOUR_ANALYSIS === 'true';

function ColourAnalysisMenuItem() {
  return (
    <button
      type="button"
      className="w-full flex items-center justify-between p-5 sm:p-6 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left"
      onClick={() => window.location.assign('/?feature=colour-analysis&view=saved')}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#EDE8F5] rounded-full flex items-center justify-center">
          <Palette className="w-5 h-5 text-[#6E5A86]" aria-hidden="true" />
        </div>
        <div>
          <div className="font-medium text-[#2A324B]">Colour analysis</div>
          <div className="text-sm text-[#2A324B]/70">See your colours or start a new analysis</div>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-[#2A324B]/40" aria-hidden="true" />
    </button>
  );
}

// Strip stray markdown markers (`**`, `*`) from saved-product notes so they
// don't surface as literal asterisks. We keep the cleaned text — bolding would
// need a markdown renderer, which is overkill here.
function stripMarkdown(text) {
  if (typeof text !== 'string') return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(?<![*\w])\*(?!\s)(.+?)(?<!\s)\*(?![*\w])/g, '$1')
    .trim();
}

// Split dense single-paragraph notes (e.g. "Why it works: … Texture/finish: …
// How to apply: …") into bullet items keyed off the inline labels. Falls back
// to the cleaned paragraph when no labels are detected.
function notesToBullets(text) {
  const clean = stripMarkdown(text);
  if (!clean) return null;
  const explicitLines = clean.split(/\n+/).map(l => l.trim()).filter(Boolean);
  if (explicitLines.length >= 2) return explicitLines;
  // Inline-labels pattern e.g. "Why it works: …" / "Texture/finish: …"
  const labelSplit = clean.split(/\s+(?=(?:Why it works|Why it matches|Texture\/finish|Texture|How to apply|How to use|Best for|Recommended shade|Tip|Notes?):\s)/);
  if (labelSplit.length >= 2) return labelSplit.map(s => s.trim()).filter(Boolean);
  return null;
}

// Shelf thumbnail: preserve real images and use the shared illustration when absent.
function ProductMonogram({ product, size = 64, rounded = 16 }) {
  const src = product?.catalog_image_url || product?.image_url || product?.image || product?.image_base64 || null;
  return (
    <div style={{ width: size, height: size, borderRadius: rounded, overflow: 'hidden', flexShrink: 0 }}>
      <CatalogProductImage src={src} alt={product?.name || ''} brand={product?.brand || ''} className="w-full h-full object-cover" />
    </div>
  );
}

// Placeholder when product has no valid image URL (used in legacy list views)
// 2026-06-09 (Sofia #8): bumped from w-12/h-12 to w-16/h-16 so the product
// photo is the recognisable element on list cards, matching the basket
// reference she shared. Same XL treatment for the placeholder.
const ProductImageOrPlaceholder = ({ product }) => {
  // Check all possible image fields in priority order
  const src = product.catalog_image_url || product.image_url || product.image || product.image_base64 || null;
  const isValid = src && typeof src === 'string' && src.length > 0;

  const placeholder = (
    <div className="w-16 h-16 rounded-xl bg-[#F3D5B5]/20 flex items-center justify-center flex-shrink-0">
      <Package2 className="h-7 w-7 text-[#2A324B]/40" />
    </div>
  );
  if (!isValid) return placeholder;
  return (
    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 relative bg-[#F3D5B5]/20">
      <img
        src={src}
        alt={product.name}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.parentElement?.classList.add('fallback-placeholder');
        }}
      />
    </div>
  );
};

// ── Reusable saved-product card (Figma style, no price) ───────────────────────
const StarRating = ({ rating, max = 5 }) => {
  const val = parseFloat(rating) || 0;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <svg key={i} className={`h-3 w-3 ${i < Math.round(val) ? 'text-yellow-400' : 'text-[#D0D0D0]'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {val > 0 && <span className="text-[#2A324B]/50 text-xs ml-1">{val}</span>}
    </div>
  );
};

const SavedProductCard = ({ product, onClick, onAnalyse }) => {
  const src = product.catalog_image_url || product.image_url || product.image || product.image_base64 || null;
  return (
    <div className="bg-white rounded-2xl border border-[#EAE0D4] overflow-hidden">
      <button
        onClick={onClick}
        className="w-full text-left p-3.5 flex gap-3.5 hover:bg-[#FBF7F2] transition-colors active:scale-[0.99]"
      >
        {/* Thumbnail — photo or elegant brand-initial tile */}
        <ProductMonogram product={product} size={56} rounded={14} />

        {/* Info */}
        <div className="flex-1 min-w-0 py-0.5">
          {/* 2026-06-09 (Sofia): product name now in the default sans
              (DM Sans) to match the rest of the app — the serif read as
              inconsistent. */}
          <p className="text-[15px] font-semibold text-[#2A1212] leading-snug truncate">{product.name || 'Unnamed'}</p>
          {product.brand && <p className="text-[13px] uppercase tracking-wider text-[#2A324B]/40 truncate mt-0.5">{product.brand}</p>}

          {/* Rating — only when actually rated (no empty star rows) */}
          {parseFloat(product.rating) > 0 && (
            <div className="mt-1.5">
              <StarRating rating={product.rating} />
            </div>
          )}

          {/* Category + usage */}
          {(product.category || product.usage) && (
            <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
              {product.category && (
                <span className="text-[13px] font-medium bg-[#F3D5B5]/50 text-[#2A324B]/70 px-2 py-0.5 rounded-full leading-none capitalize">
                  {product.category}
                </span>
              )}
              {product.usage && (
                <span className="flex items-center gap-1 text-[13px] text-[#2A324B]/45">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {product.usage}
                </span>
              )}
            </div>
          )}

          {/* 2026-06-09 (Sofia #9): verbose "AI Score: 78% - reasoning…" notes
              line REMOVED — was the wall of text she flagged. Score is now
              shown as a tiny chip on the right (added below). User taps the
              card to see the full analysis. */}
        </div>

        {/* Score chip on the right edge — replaces the verbose paragraph */}
        {(() => {
          try {
            const a = typeof product.ai_analysis === 'string' ? JSON.parse(product.ai_analysis) : product.ai_analysis;
            if (typeof a?.score === 'number') {
              return (
                <span className="self-center text-[13px] font-semibold text-[#2A324B] bg-[#F3D5B5]/40 px-2 py-0.5 rounded-full flex-shrink-0">
                  {a.score}%
                </span>
              );
            }
          } catch (_) { /* ignore */ }
          return null;
        })()}

        <svg className="w-4 h-4 text-[#2A324B]/20 flex-shrink-0 self-center" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* 2026-06-09 (Sofia #9): "AI Analysis" lightbulb button REMOVED.
          The whole card is tappable — user taps to open the product and see
          the full analysis. No need for a redundant inner button. */}
    </div>
  );
};

// ── Compact saved-product detail view ─────────────────────────────────────────
const SavedProductDetailView = ({ product, category, onBack, onSave, onDelete, onAnalyse }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: product?.name || '',
    brand: product?.brand || '',
    category: product?.category || '',
    usage: product?.usage || '',
    rating: product?.rating || '',
    notes: product?.notes || '',
    recommended_shade: product?.recommended_shade || '',
  });

  const src = product?.catalog_image_url || product?.image_url || product?.image || product?.image_base64 || null;

  const handleSave = () => {
    if (onSave) onSave({ ...product, ...form });
    setEditing(false);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="bg-white px-4 pt-4 pb-3 flex-shrink-0 flex items-center gap-3 border-b border-[#F0F0F0]">
        <button onClick={onBack} className="p-1.5 hover:bg-[#F5F5F5] rounded-xl transition-colors">
          <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2 className="flex-1 font-bold text-[#2A324B] text-xl leading-tight">Product Details</h2>
        <button
          onClick={() => editing ? handleSave() : setEditing(true)}
          className="text-xs font-semibold bg-[#F5F5F5] hover:bg-[#EDEDED] text-[#2A324B] px-3 py-1.5 rounded-xl transition-colors"
        >
          {editing ? 'Save' : 'Edit'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {/* Image — photo, or an elegant brand-initial tile */}
        <div className="rounded-2xl overflow-hidden flex items-center justify-center border border-[#EAE0D4]" style={{ minHeight: 180, background: '#FBF7F2' }}>
          {src ? (
            <img src={src} alt={form.name} className="w-full max-h-52 object-contain p-4" />
          ) : (
            <span style={{ fontFamily: SERIF_FF, fontWeight: 500, fontSize: 64, color: '#C2A179' }}>
              {((product?.brand || product?.name || '·').trim()[0] || '·').toUpperCase()}
            </span>
          )}
        </div>

        {/* Fields */}
        <div className="bg-white rounded-2xl divide-y divide-[#F5F5F5] overflow-hidden">
          {[
            { label: 'Product name', key: 'name' },
            { label: 'Brand', key: 'brand' },
            { label: 'Category', key: 'category' },
            { label: 'Recommended shade', key: 'recommended_shade', placeholder: 'e.g. 230N, Light Neutral', hideWhenEmpty: true },
            { label: 'Usage', key: 'usage', placeholder: 'e.g. Morning & Night' },
            { label: 'Notes', key: 'notes', placeholder: 'Add a personal note…' },
          ].filter(({ key, hideWhenEmpty }) => !hideWhenEmpty || editing || form[key]).map(({ label, key, placeholder }) => (
            <div key={key} className="px-4 py-3">
              <p className="text-[13px] font-semibold uppercase tracking-wider text-[#2A324B]/35 mb-1">{label}</p>
              {editing ? (
                <input
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={placeholder || label}
                  className="w-full text-sm text-[#2A324B] bg-[#F5F5F5] rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-[#F3D5B5]/60"
                />
              ) : key === 'notes' ? (
                form[key] ? (() => {
                  const bullets = notesToBullets(form[key]);
                  return bullets ? (
                    <ul className="space-y-1.5">
                      {bullets.map((b, i) => (
                        <li key={i} className="flex gap-2 text-sm text-[#2A324B]/70 leading-relaxed">
                          <span className="text-[#C8956A] flex-shrink-0 mt-0.5">•</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-[#2A324B]/70 leading-relaxed whitespace-pre-wrap">{stripMarkdown(form[key])}</p>
                  );
                })() : <span className="text-sm text-[#2A324B]/25">—</span>
              ) : (
                <p className={`text-sm ${!form[key] ? 'text-[#2A324B]/25' : 'font-medium text-[#2A324B]'}`}>
                  {form[key] || <span className="text-[#2A324B]/25">—</span>}
                </p>
              )}
            </div>
          ))}

          {/* Rating */}
          <div className="px-4 py-3">
            <p className="text-[13px] font-semibold uppercase tracking-wider text-[#2A324B]/35 mb-1.5">Rating</p>
            {editing ? (
              <div className="flex gap-2">
                {[1,2,3,4,5].map(n => (
                  <button key={n} type="button" onClick={() => setForm(f => ({ ...f, rating: n }))}
                    className={`w-8 h-8 rounded-lg border text-sm font-medium transition-all ${parseInt(form.rating) >= n ? 'bg-yellow-400 border-yellow-400 text-white' : 'border-[#E0E0E0] text-[#2A324B]/40'}`}>
                    {n}
                  </button>
                ))}
              </div>
            ) : parseFloat(form.rating) > 0 ? (
              <StarRating rating={form.rating} />
            ) : (
              <span className="text-sm text-[#2A324B]/25">—</span>
            )}
          </div>
        </div>

        {/* Analyse with AI */}
        {onAnalyse && (
          <button
            onClick={() => onAnalyse(product)}
            className="w-full py-3 flex items-center justify-center gap-2 bg-[#2A324B] hover:bg-[#1E2438] text-white text-sm font-semibold rounded-2xl transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            Analyse with AI
          </button>
        )}

        {/* Delete */}
        {onDelete && (
          <button
            onClick={() => onDelete(product)}
            className="w-full py-2.5 text-sm text-red-400 hover:text-red-500 font-medium transition-colors"
          >
            Remove from list
          </button>
        )}
      </div>
    </div>
  );
};

const MenuSheet = ({ isOpen, onClose, currentView, onNavigate, savedProducts, saveProduct, updateProduct, deleteProduct, customListNames, createCustomList, renameCustomList, deleteCustomList, onLoadProducts, onContinueToChat, isAnonymous = false, onShowSignIn, onSignInDirect, onGatedAction, userId, initialView = 'menu', onImageConsentChange, onProfileSave, hasImageConsent = false, requestImageConsent }) => {
  const [activeView, setActiveView] = useState(initialView);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // 'success', 'error', null
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [analyzeProduct, setAnalyzeProduct] = useState(null);
  const [routineAddProduct, setRoutineAddProduct] = useState(null); // Shelf "Add to routine" -> insert into SkinRoutine (#36)
  // Add-to-list UX state
  const [addToListCategory, setAddToListCategory] = useState(null); // e.g. 'current', 'wishlist', 'custom1'
  // When the user picks "Take a picture" from the add-to-list modal, we
  // remember which list they came from. ProductAnalysisView reads this and
  // saves straight to that list instead of asking again. Cleared after the
  // analyse-product view closes (in handleBackToMenu / the onBack chain).
  const [analyseTargetList, setAnalyseTargetList] = useState(null);
  const [addMode, setAddMode] = useState(null); // 'choose' | 'manual'
  const [manualName, setManualName] = useState('');
  const [manualBrand, setManualBrand] = useState('');

  const openAddToList = (category) => {
    setAddToListCategory(category);
    setAddMode('choose');
    setManualName('');
    setManualBrand('');
  };
  const closeAddToList = () => {
    setAddToListCategory(null);
    setAddMode(null);
    setManualName('');
    setManualBrand('');
  };
  const submitManualAdd = async () => {
    if (!manualName.trim() || !addToListCategory) return;
    const product = {
      id: `manual_${Date.now()}`,
      name: manualName.trim(),
      brand: manualBrand.trim(),
      source: 'manual',
    };
    if (saveProduct) await saveProduct(product, addToListCategory);
    closeAddToList();
  };

  // Sync activeView when menu opens with a specific initialView
  useEffect(() => {
    if (isOpen) setActiveView(initialView);
  }, [isOpen, initialView]);

  if (!isOpen) return null;

  const handleBackToMenu = () => {
    setActiveView('menu');
    setSelectedProduct(null);
    setSelectedCategory(null);
  };

  const handleProductClick = (product, category) => {
    setSelectedProduct(product);
    setSelectedCategory(category);
    setActiveView('saved-product-detail');
  };

  const handleProductSave = (updatedProduct) => {
    // Update the product in the appropriate category
    if (selectedProduct?.id && updateProduct) {
      updateProduct(selectedProduct.id, updatedProduct);
      setActiveView(selectedCategory + '-list');
      setSelectedProduct(null);
      setSelectedCategory(null);
    }
  };

  const handleProductDelete = async (productOrId) => {
    // Callers pass either a product object (SavedProductDetailView) or an id
    // (ProductDetail). Normalize to the id — deleteProduct matches on it.
    const productId = (productOrId && typeof productOrId === 'object') ? productOrId.id : productOrId;
    if (!productId || !deleteProduct) return;
    try {
      await deleteProduct(productId);
    } catch (err) {
      console.error('Failed to remove product:', err);
    }
    setActiveView((selectedCategory || 'my-products') + '-list');
    setSelectedProduct(null);
    setSelectedCategory(null);
  };

  const handleCreateList = () => {
    if (newListName.trim() && createCustomList) {
      const listKey = createCustomList(newListName.trim());
      if (listKey) {
        setActiveView(listKey + '-list');
        setShowCreateListModal(false);
        setNewListName('');
      }
    }
  };

  const getAvailableSlots = () => {
    const slots = [];
    if (customListNames.custom1 === 'My Custom List 1' && (savedProducts.custom1?.length ?? 0) === 0) {
      slots.push('custom1');
    }
    if (customListNames.custom2 === 'My Custom List 2' && (savedProducts.custom2?.length ?? 0) === 0) {
      slots.push('custom2');
    }
    return slots;
  };

  const handleNotifySignup = (feature) => {
    // Simple approach: use browser prompt to collect email
    const email = prompt(`Enter your email to be notified when ${feature} is available:`);
    
    if (email && email.includes('@')) {
      try {
        // Submit to Mailchimp form using hidden form submission
        const form = document.createElement('form');
        form.action = 'https://carrd.us2.list-manage.com/subscribe/post?u=dcbc55153aa71560412f8f8f6&id=8bdb284d16&f_id=002369e1f0';
        form.method = 'POST';
        form.target = '_blank';
        form.style.display = 'none';
        
        // Add email field
        const emailField = document.createElement('input');
        emailField.type = 'email';
        emailField.name = 'EMAIL';
        emailField.value = email;
        form.appendChild(emailField);
        
        // Add anti-bot field (required by Mailchimp)
        const botField = document.createElement('input');
        botField.type = 'text';
        botField.name = 'b_dcbc55153aa71560412f8f8f6_8bdb284d16';
        botField.setAttribute('tabindex', '-1');
        botField.value = '';
        botField.style.position = 'absolute';
        botField.style.left = '-5000px';
        form.appendChild(botField);
        
        // Submit form
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
        
        setSubscriptionStatus('success');
        setTimeout(() => {
          setSubscriptionStatus(null);
        }, 3000);
        
      } catch (error) {
        console.error('Subscription error:', error);
        setSubscriptionStatus('error');
        setTimeout(() => {
          setSubscriptionStatus(null);
        }, 3000);
      }
    } else if (email) {
      // Invalid email
      setSubscriptionStatus('error');
      setTimeout(() => {
        setSubscriptionStatus(null);
      }, 3000);
    }
    // If user cancels (email is null), do nothing
  };


  const renderContent = () => {
    switch (activeView) {
      case 'personal-profile':
        return (
          <PersonalProfile
            onBack={handleBackToMenu}
            onContinueToChat={onContinueToChat}
            onSave={onProfileSave}
            userId={userId}
          />
        );
      
      case 'how-it-works':
        return (
          <div className="h-full flex flex-col">
            <div className="px-4 pt-3 pb-2 border-b border-[#EAE0D4]">
              <SectionHeader title="How Son:mi works" onBack={handleBackToMenu} />
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-[#341C1C] mb-2">What Son:mi does</h3>
                <p className="text-sm text-[#2A324B]/70 leading-relaxed">
                  Son:mi uses AI to provide personalised skincare and beauty information based on your skin analysis, profile, and the products you share with us. It can suggest shades, flag ingredients, and help you build a routine tailored to you.
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#341C1C] mb-2">Independent recommendations</h3>
                <p className="text-sm text-[#2A324B]/70 leading-relaxed">
                  Every product and brand Son:mi suggests is chosen purely on how well it fits your skin — <span className="font-medium text-[#341C1C]">none are paid promotions and we earn no commission or revenue from recommending them.</span> Our only goal is what's right for your skin.
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#341C1C] mb-2">Important limitations</h3>
                <p className="text-sm text-[#2A324B]/70 leading-relaxed">
                  Son:mi may not catch every ingredient or flag every sensitivity. Product formulations change, labels vary by region, and AI has limits. <span className="font-medium text-[#341C1C]">Always read the full ingredient list before use</span>, especially if you have known allergies or sensitivities.
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#341C1C] mb-2">Not medical advice</h3>
                <p className="text-sm text-[#2A324B]/70 leading-relaxed">
                  Son:mi provides information for educational purposes only. Nothing here constitutes medical or dermatological advice. If you have a skin condition, allergy, or medical concern, please consult a qualified professional.
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#341C1C] mb-2">Shade matching</h3>
                <p className="text-sm text-[#2A324B]/70 leading-relaxed">
                  Shade recommendations are based on your selfie analysis and our product catalogue. Lighting, screen calibration, and photo quality can affect results. We recommend using our suggestions as a starting point rather than a definitive match.
                </p>
              </div>
              {/* Prominent allergy warning */}
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
                  <div>
                    <p className="text-sm font-semibold text-amber-900 mb-1">Allergy & reaction warning</p>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      Son:mi <strong>cannot guarantee</strong> it will detect every allergen. Always read the full ingredient list before using any product. If you have a known allergy or experience a reaction to any product Son:mi has recommended, seek medical advice.
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-[#341C1C]/8">
                <p className="text-sm text-[#2A324B]/60 leading-relaxed">
                  By using Son:mi you agree to our{' '}
                  <a href="https://sonmi.io/terms" target="_blank" rel="noopener noreferrer" className="underline font-medium">Terms</a>
                  {' '}and{' '}
                  <a href="https://sonmi.io/privacy" target="_blank" rel="noopener noreferrer" className="underline font-medium">Privacy Notice</a>.
                </p>
              </div>
            </div>
          </div>
        );

      case 'account-settings':
        return (
          <AccountSettings
            onBack={handleBackToMenu}
            onImageConsentChange={onImageConsentChange}
            onContinueToChat={onContinueToChat}
          />
        );
      
      case 'catalog':
        // Redesigned landing page (feature-flagged); fallback to ProductCatalog in production
        if (process.env.REACT_APP_ENABLE_SKIN_PROGRESS !== 'true') {
          return (
            <div className="h-full flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6">
                <ProductCatalog
                  onBack={handleBackToMenu}
                  savedProducts={savedProducts}
                  saveProduct={saveProduct}
                  updateProduct={updateProduct}
                  deleteProduct={deleteProduct}
                  onContinueToChat={onContinueToChat}
                  onOpenRoutine={(prod) => { if (prod) setRoutineAddProduct(prod); setActiveView('routine'); }}
                  isAnonymous={isAnonymous}
                  onShowSignIn={onShowSignIn}
                  onGatedAction={onGatedAction}
                  userId={userId}
                />
              </div>
            </div>
          );
        }
        return (
          <div className="h-full flex flex-col bg-white">
            <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-[#EAE0D4]">
              <SectionHeader title="Analyse a Product" subtitle="See how any product suits your skin" onBack={handleBackToMenu} />
            </div>

            <div className="flex-1 overflow-y-auto px-4 pt-4 pb-6 space-y-3">
              {/* Equal-size action cards in a grid */}
              <div className="grid grid-cols-2 gap-3">
                {/* Analyse a new product → opens ProductAnalysisView directly */}
                <button
                  onClick={() => setActiveView('product-analysis')}
                  className="bg-white rounded-2xl p-5 shadow-sm text-left flex flex-col gap-3 hover:bg-[#FAFAFA] transition-colors border border-[#F3D5B5]/40 active:scale-[0.98]"
                >
                  <div className="w-11 h-11 rounded-2xl bg-[#FDF6EE] flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#C8956A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-[#2A324B] text-sm leading-snug">Analyse a product</p>
                    <p className="text-xs text-[#2A324B]/45 mt-1 leading-snug">Ingredients, shade match & skin compatibility</p>
                  </div>
                </button>

                {/* Compare products → opens ProductComparisonView directly */}
                <button
                  onClick={() => setActiveView('product-comparison')}
                  className="bg-white rounded-2xl p-5 shadow-sm text-left flex flex-col gap-3 hover:bg-[#FAFAFA] transition-colors active:scale-[0.98]"
                >
                  <div className="w-11 h-11 rounded-2xl bg-[#F0F4FF] flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#5C6BC0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-[#2A324B] text-sm leading-snug">Compare products</p>
                    <p className="text-xs text-[#2A324B]/45 mt-1 leading-snug">Side-by-side ingredient & shade analysis</p>
                  </div>
                </button>
              </div>

              {/* Recently analysed products (from localStorage) */}
              {(() => {
                let recent = [];
                try {
                  recent = JSON.parse(localStorage.getItem('sonmi_recent_analyses_' + (userId || 'guest')) || '[]').slice(0, 10);
                } catch {}
                return (
                  <div className="pt-1">
                    <div className="flex items-center justify-between mb-2.5">
                      <p className="text-xs font-semibold text-[#2A324B]/40 uppercase tracking-wide">Recently analysed</p>
                    </div>
                    {recent.length === 0 ? (
                      <div className="bg-white rounded-2xl shadow-sm px-4 py-6 flex flex-col items-center text-center gap-2">
                        <div className="w-10 h-10 rounded-2xl bg-[#F3D5B5]/20 flex items-center justify-center">
                          <svg className="w-5 h-5 text-[#2A324B]/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <p className="text-sm font-medium text-[#2A324B]/50">No analyses yet</p>
                        <p className="text-xs text-[#2A324B]/35">Analyse a product above and it'll appear here</p>
                      </div>
                    ) : (
                      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        {recent.map((p, i) => {
                          const hasImg = p.image && typeof p.image === 'string' && p.image.startsWith('http');
                          const scoreColor = p.matchScore >= 80 ? 'text-green-600' : p.matchScore >= 60 ? 'text-amber-600' : 'text-red-500';
                          return (
                            <button key={p.id || i}
                              onClick={() => { trackRecentAnalysisOpened(); setAnalyzeProduct(p); setActiveView('product-analysis'); }}
                              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAFA] transition-colors text-left ${i < recent.length - 1 ? 'border-b border-[#F8F8F8]' : ''}`}>
                              <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                                <CatalogProductImage src={hasImg ? p.image : ''} alt={p.name || 'Product'} brand={p.brand || ''} className="w-full h-full object-cover" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#2A324B] truncate">{p.name || 'Unnamed'}</p>
                                <p className="text-xs text-[#2A324B]/45 truncate">{p.brand || p.category || ''}</p>
                              </div>
                              {p.matchScore != null && (
                                <span className={`text-xs font-semibold flex-shrink-0 ${scoreColor}`}>{p.matchScore}%</span>
                              )}
                              <svg className="w-4 h-4 text-[#2A324B]/20 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        );

      case 'product-analysis':
        // 2026-06-09 (Sofia): removed the p-6 wrapper that boxed the full-bleed
        // ProductAnalysisRedesign result view (the visible "frame"/border she
        // kept flagging). The inner views manage their own padding now —
        // selection/upload screen has px-4, result screen is edge-to-edge.
        return (
          <div className="h-full overflow-hidden">
            <ProductCatalog
              onBack={() => {
                setAnalyzeProduct(null);
                setAnalyseTargetList(null);  // clear so next entry to analyse-product asks normally
                setActiveView('catalog');
              }}
              savedProducts={savedProducts}
              saveProduct={saveProduct}
              updateProduct={updateProduct}
              deleteProduct={deleteProduct}
              onContinueToChat={onContinueToChat}
              isAnonymous={isAnonymous}
              onShowSignIn={onShowSignIn}
              onGatedAction={onGatedAction}
              userId={userId}
              initialView="product-analysis"
              initialAnalysisProduct={analyzeProduct}
              initialAnalyseTargetList={analyseTargetList}
            />
          </div>
        );

      case 'product-comparison':
        // 2026-06-09 (Sofia): removed the p-6 frame around the full-bleed
        // ComparisonResultsRedesign. Inner views own their padding.
        return (
          <div className="h-full overflow-hidden">
            <ProductCatalog
              onBack={() => setActiveView('catalog')}
              savedProducts={savedProducts}
              saveProduct={saveProduct}
              updateProduct={updateProduct}
              deleteProduct={deleteProduct}
              onContinueToChat={onContinueToChat}
              isAnonymous={isAnonymous}
              onShowSignIn={onShowSignIn}
              onGatedAction={onGatedAction}
              userId={userId}
              initialView="product-comparison"
            />
          </div>
        );

      case 'catalog-browse':
        return (
          <div className="h-full flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6">
              <ProductCatalog
                onBack={() => setActiveView('catalog')}
                savedProducts={savedProducts}
                saveProduct={saveProduct}
                updateProduct={updateProduct}
                deleteProduct={deleteProduct}
                onContinueToChat={onContinueToChat}
                isAnonymous={isAnonymous}
                onShowSignIn={onShowSignIn}
                onGatedAction={onGatedAction}
                userId={userId}
              />
            </div>
          </div>
        );

      case 'shelf':
        // #55: hero-image-led shelf — Owned/Wishlist tabs + Skincare/Makeup
        // filter. This is what the "My Products" menu button now opens.
        // renderContent() mounts into an overflow-hidden sheet, so each view
        // owns its own scroll container.
        return (
          <div className="h-full overflow-y-auto bg-white px-4 pt-4 pb-6">
            <ShelfView
              products={savedProducts}
              onBack={handleBackToMenu}
              onSelectProduct={(p) => {
                setSelectedProduct(p);
                setSelectedCategory('shelf');
                setActiveView('product-detail');
              }}
              onRemoveProduct={async (p) => {
                // Delete in place — stay on the shelf (don't navigate away like
                // handleProductDelete does). savedProducts updates → re-renders.
                const id = p && typeof p === 'object' ? p.id : p;
                if (id && deleteProduct) {
                  try { await deleteProduct(id); } catch (e) { console.error('Remove from shelf failed:', e); }
                }
              }}
              onOpenRoutine={() => setActiveView('routine')}
            />
          </div>
        );

      case 'my-products':
        // Redesigned My Products (feature-flagged); fallback to original list in production
        if (process.env.REACT_APP_ENABLE_SKIN_PROGRESS !== 'true') {
          return (
            <div className="h-full flex flex-col">
              <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-[#EAE0D4]">
                <SectionHeader title="My Products" subtitle="Your saved and analysed products" onBack={handleBackToMenu} />
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-2">
                {[
                  { key: 'favourites', label: 'Favourites', view: 'favourites-list', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
                  { key: 'current', label: 'My shelf', view: 'current-list', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
                  { key: 'wishlist', label: 'Wishlist', view: 'wishlist-list', icon: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z' },
                  ...(customListNames?.custom1 ? [{ key: 'custom1', label: customListNames.custom1, view: 'custom1-list', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' }] : []),
                  ...(customListNames?.custom2 ? [{ key: 'custom2', label: customListNames.custom2, view: 'custom2-list', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' }] : []),
                ].map(({ key, label, view, icon }) => {
                  const count = (savedProducts?.[key] || []).length;
                  return (
                    <button key={key} onClick={() => setActiveView(view)}
                      className="w-full flex items-center justify-between p-4 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={icon} />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-[#2A324B]">{label}</div>
                          <div className="text-sm text-[#2A324B]/50">{count} product{count !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        return (
          <div className="h-full flex flex-col bg-white">
            <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-[#EAE0D4]">
              <SectionHeader title="My Products" subtitle="Your product lists" onBack={handleBackToMenu} />
            </div>
            <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-2">
              {/* My shelf */}
              {(() => {
                const count = (savedProducts?.current || []).length;
                return (
                  <button onClick={() => setActiveView('current-list')}
                    className="w-full flex items-center justify-between p-4 bg-white hover:bg-[#F3D5B5]/10 rounded-2xl shadow-sm transition-colors text-left">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-[#2A324B]">My shelf</div>
                        <div className="text-sm text-[#2A324B]/50">{count} product{count !== 1 ? 's' : ''}</div>
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })()}
              {/* Wishlist */}
              {(() => {
                const count = (savedProducts?.wishlist || []).length;
                return (
                  <button onClick={() => setActiveView('wishlist-list')}
                    className="w-full flex items-center justify-between p-4 bg-white hover:bg-[#F3D5B5]/10 rounded-2xl shadow-sm transition-colors text-left">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-[#2A324B]">Wishlist</div>
                        <div className="text-sm text-[#2A324B]/50">{count} product{count !== 1 ? 's' : ''}</div>
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })()}
              {/* Custom lists — only show if user has named them or they have products */}
              {[
                { key: 'custom1', label: customListNames?.custom1, defaultLabel: 'My Custom List 1' },
                { key: 'custom2', label: customListNames?.custom2, defaultLabel: 'My Custom List 2' },
              ].filter(({ label, defaultLabel, key }) => {
                const count = (savedProducts?.[key] || []).length;
                return label && (label !== defaultLabel || count > 0);
              }).map(({ key, label }) => {
                const count = (savedProducts?.[key] || []).length;
                return (
                  <div key={key} className="flex items-center gap-2">
                    <button onClick={() => setActiveView(key + '-list')}
                      className="flex-1 flex items-center gap-3 p-4 bg-white hover:bg-[#F3D5B5]/10 rounded-2xl shadow-sm transition-colors text-left">
                      <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#2A324B] truncate">{label}</div>
                        <div className="text-sm text-[#2A324B]/50">{count} product{count !== 1 ? 's' : ''}</div>
                      </div>
                      <svg className="w-5 h-5 text-[#2A324B]/40 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    {/* Delete list button */}
                    <button
                      onClick={() => { if (deleteCustomList) deleteCustomList(key); }}
                      className="p-2.5 hover:bg-red-50 rounded-xl transition-colors group flex-shrink-0"
                      title="Remove list">
                      <svg className="w-4 h-4 text-[#2A324B]/25 group-hover:text-red-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                );
              })}
              {/* Add a list — only if fewer than 2 custom lists are active */}
              {(() => {
                const custom1Active = customListNames?.custom1 && (customListNames.custom1 !== 'My Custom List 1' || (savedProducts?.custom1 || []).length > 0);
                const custom2Active = customListNames?.custom2 && (customListNames.custom2 !== 'My Custom List 2' || (savedProducts?.custom2 || []).length > 0);
                const activeCount = [custom1Active, custom2Active].filter(Boolean).length;
                if (activeCount >= 2) return null;
                return (
                  <button
                    onClick={() => setShowCreateListModal(true)}
                    className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-[#E0E0E0] hover:border-[#2A324B]/20 hover:bg-[#FAFAFA] transition-colors text-left mt-1">
                    <div className="w-10 h-10 rounded-full bg-[#F5F5F5] flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-[#2A324B]/60">Add a list</p>
                      <p className="text-sm text-[#2A324B]/35">Up to {2 - activeCount} more custom {2 - activeCount === 1 ? 'list' : 'lists'}</p>
                    </div>
                  </button>
                );
              })()}
            </div>
          </div>
        );

      case 'favourites-list':
        return (
          <div className="h-full flex flex-col bg-white">
            <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-[#EAE0D4]">
              <SectionHeader title="Favourites" onBack={() => setActiveView('my-products')}
                right={<button onClick={() => openAddToList('favourites')} className="h-11 px-3 text-[13px] font-semibold text-[#2A1212]">+ Add</button>} />
            </div>
            <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-2">
              {savedProducts.favourites.length > 0 ? (
                savedProducts.favourites.map((product, index) => (
                  <SavedProductCard key={product.id || index} product={product} onClick={() => handleProductClick(product, 'favourites')} onAnalyse={(p) => { setAnalyzeProduct(p); setActiveView('product-analysis'); }} />
                ))
              ) : (
                <div className="bg-white rounded-2xl shadow-sm px-4 py-8 flex flex-col items-center text-center gap-2 mt-2">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#F3D5B5]/20 flex items-center justify-center mb-1">
                    <svg className="w-6 h-6 text-[#2A324B]/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[#2A324B]/60">No favourites yet</p>
                  <p className="text-xs text-[#2A324B]/35">Save products from chat to build your collection</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'current-list':
        return (
          <div className="h-full flex flex-col bg-white">
            <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-[#EAE0D4]">
              <SectionHeader title="My Shelf" onBack={() => setActiveView('my-products')}
                right={<button onClick={() => openAddToList('current')} className="h-11 px-3 text-[13px] font-semibold text-[#2A1212]">+ Add</button>} />
            </div>
            <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-2">
              {savedProducts.current.length > 0 ? (
                savedProducts.current.map((product, index) => (
                  <SavedProductCard key={product.id || index} product={product} onClick={() => handleProductClick(product, 'current')} onAnalyse={(p) => { setAnalyzeProduct(p); setActiveView('product-analysis'); }} />
                ))
              ) : (
                <div className="bg-white rounded-2xl shadow-sm px-4 py-8 flex flex-col items-center text-center gap-2 mt-2">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#F3D5B5]/20 flex items-center justify-center mb-1">
                    <svg className="w-6 h-6 text-[#2A324B]/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[#2A324B]/60">Nothing here yet</p>
                  <p className="text-xs text-[#2A324B]/35">Save products you're using to track your routine</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'wishlist-list':
        return (
          <div className="h-full flex flex-col bg-white">
            <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-[#EAE0D4]">
              <SectionHeader title="Wishlist" onBack={() => setActiveView('my-products')}
                right={<button onClick={() => openAddToList('wishlist')} className="h-11 px-3 text-[13px] font-semibold text-[#2A1212]">+ Add</button>} />
            </div>
            <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-2">
              {savedProducts.wishlist.length > 0 ? (
                savedProducts.wishlist.map((product, index) => (
                  <SavedProductCard key={product.id || index} product={product} onClick={() => handleProductClick(product, 'wishlist')} onAnalyse={(p) => { setAnalyzeProduct(p); setActiveView('product-analysis'); }} />
                ))
              ) : (
                <div className="bg-white rounded-2xl shadow-sm px-4 py-8 flex flex-col items-center text-center gap-2 mt-2">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#F3D5B5]/20 flex items-center justify-center mb-1">
                    <svg className="w-6 h-6 text-[#2A324B]/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[#2A324B]/60">Wishlist is empty</p>
                  <p className="text-xs text-[#2A324B]/35">Save products you want to try</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'saved-product-detail':
        return (
          <SavedProductDetailView
            product={selectedProduct}
            category={selectedCategory}
            onBack={() => {
              setActiveView((selectedCategory || 'my-products') + '-list');
              setSelectedProduct(null);
              setSelectedCategory(null);
            }}
            onSave={handleProductSave}
            onDelete={handleProductDelete}
            onAnalyse={(product) => {
              setAnalyzeProduct(product);
              setActiveView('product-analysis');
            }}
          />
        );
      case 'product-detail':
        return (
          <ProductDetail
            product={selectedProduct}
            userId={userId}
            onBack={() => {
              setActiveView(selectedCategory === 'shelf' ? 'shelf' : selectedCategory + '-list');
              setSelectedProduct(null);
              setSelectedCategory(null);
            }}
            onSave={handleProductSave}
            onDelete={handleProductDelete}
            onAnalysisComplete={(result) => {
              // Update the product in local state with the analysis result
              if (selectedProduct && updateProduct) {
                updateProduct(selectedProduct.id, { ...selectedProduct, ai_analysis: result });
              }
            }}
          />
        );
      case 'custom1-list':
        return (
          <div className="h-full flex flex-col bg-white">
            <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-[#EAE0D4]">
              <SectionHeader title={customListNames.custom1} onBack={() => setActiveView('my-products')}
                right={<button onClick={() => openAddToList('custom1')} className="h-11 px-3 text-[13px] font-semibold text-[#2A1212]">+ Add</button>} />
            </div>
            <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-2">
              {(savedProducts.custom1?.length ?? 0) > 0 ? (
                (savedProducts.custom1 ?? []).map((product, index) => (
                  <SavedProductCard key={product.id || index} product={product} onClick={() => handleProductClick(product, 'custom1')} onAnalyse={(p) => { setAnalyzeProduct(p); setActiveView('product-analysis'); }} />
                ))
              ) : (
                <div className="bg-white rounded-2xl shadow-sm px-4 py-8 flex flex-col items-center text-center gap-2 mt-2">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#F3D5B5]/20 flex items-center justify-center mb-1">
                    <svg className="w-6 h-6 text-[#2A324B]/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[#2A324B]/60">No products yet</p>
                  <p className="text-xs text-[#2A324B]/35">Tap + Add to start building this list</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'custom2-list':
        return (
          <div className="h-full flex flex-col bg-white">
            <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-[#EAE0D4]">
              <SectionHeader title={customListNames.custom2} onBack={() => setActiveView('my-products')}
                right={<button onClick={() => openAddToList('custom2')} className="h-11 px-3 text-[13px] font-semibold text-[#2A1212]">+ Add</button>} />
            </div>
            <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-2">
              {(savedProducts.custom2?.length ?? 0) > 0 ? (
                (savedProducts.custom2 ?? []).map((product, index) => (
                  <SavedProductCard key={product.id || index} product={product} onClick={() => handleProductClick(product, 'custom2')} onAnalyse={(p) => { setAnalyzeProduct(p); setActiveView('product-analysis'); }} />
                ))
              ) : (
                <div className="bg-white rounded-2xl shadow-sm px-4 py-8 flex flex-col items-center text-center gap-2 mt-2">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#F3D5B5]/20 flex items-center justify-center mb-1">
                    <svg className="w-6 h-6 text-[#2A324B]/25" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-[#2A324B]/60">No products yet</p>
                  <p className="text-xs text-[#2A324B]/35">Tap + Add to start building this list</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'routine':
        return (
          <div className="h-full flex flex-col bg-white">
            <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-[#EAE0D4]">
              <SectionHeader title="My Routine" subtitle="Your daily and custom skincare steps" onBack={handleBackToMenu}
                badge={<span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0">Beta</span>}
                right={onClose && <button type="button" onClick={onClose} aria-label="Close" className="w-11 h-11 rounded-full hover:bg-[#F5F5F5] text-[#2A324B]/60 text-xl">×</button>} />
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 pt-2">
                <SkinRoutine
                  userId={userId}
                  isAnonymous={isAnonymous}
                  onShowSignIn={onShowSignIn}
                  enrichedProducts={Object.values(savedProducts || {}).flat()}
                  addProduct={routineAddProduct}
                  onProductAdded={() => setRoutineAddProduct(null)}
                />
              </div>
            </div>
          </div>
        );
      case 'progress':
        return (
          <div className="h-full flex flex-col bg-white">
            <div className="bg-white px-4 pt-3 pb-2 flex-shrink-0 border-b border-[#EAE0D4]">
              <SectionHeader title="Skin Journal" subtitle="Track your skin's progress over time" onBack={handleBackToMenu}
                badge={<span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0">Beta</span>}
                right={onClose && <button type="button" onClick={onClose} aria-label="Close" className="w-11 h-11 rounded-full hover:bg-[#F5F5F5] text-[#2A324B]/60 text-xl">×</button>} />
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 pt-2">
                <SkinTimeline
                  userId={userId}
                  hasImageConsent={hasImageConsent}
                  requestImageConsent={requestImageConsent}
                />
              </div>
            </div>
          </div>
        );
      default:
        // Locked menu for anonymous users
        if (isAnonymous) {
          return (
            <div className="h-full overflow-y-auto px-4 pt-3 pb-4">
              <SectionHeader title="Menu" subtitle="Sign in to unlock all features" onBack={onClose} className="mb-2" />
              <div className="space-y-2">
                {SHOW_COLOUR_ANALYSIS && <ColourAnalysisMenuItem />}
                {/* Analyse a Product — UNLOCKED for guests (event/demo accessibility).
                    Mirrors the authed-user button at the bottom of this file; routes to
                    the same catalog view. No lock icon, no opacity dim. */}
                <button
                  className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left"
                  onClick={() => setActiveView('catalog')}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-[#2A324B]">Analyse a Product</div>
                      <div className="text-sm text-[#2A324B]/70">See how any product matches your skin</div>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Track Skin Progress - Locked */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onShowSignIn && onShowSignIn()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onShowSignIn && onShowSignIn(); } }}
                  className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 rounded-lg bg-[#F5F5F5]/50 opacity-60 cursor-pointer hover:opacity-90 active:scale-[0.99] transition">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 flex-shrink-0 bg-[#E8E0D8]/60 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#2A324B]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-[#2A324B]/60 flex items-center gap-2">
                        Track Skin Progress
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div className="text-sm text-[#2A324B]/40">Monitor improvements and product impacts</div>
                    </div>
                  </div>
                </div>

                {/* Social Routines - Locked */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onShowSignIn && onShowSignIn()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onShowSignIn && onShowSignIn(); } }}
                  className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 rounded-lg bg-[#F5F5F5]/50 opacity-60 cursor-pointer hover:opacity-90 active:scale-[0.99] transition">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 flex-shrink-0 bg-[#E8E0D8]/60 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#2A324B]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-[#2A324B]/60 flex items-center gap-2">
                        Social Routines
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div className="text-sm text-[#2A324B]/40">Follow friends' skincare journeys</div>
                    </div>
                  </div>
                </div>

                {/* Personal Profile - Locked */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onShowSignIn && onShowSignIn()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onShowSignIn && onShowSignIn(); } }}
                  className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 rounded-lg bg-[#F5F5F5]/50 opacity-60 cursor-pointer hover:opacity-90 active:scale-[0.99] transition">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 flex-shrink-0 bg-[#E8E0D8]/60 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#2A324B]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-[#2A324B]/60 flex items-center gap-2">
                        Personal Profile
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div className="text-sm text-[#2A324B]/40">Your skincare identity & preferences</div>
                    </div>
                  </div>
                </div>

                {/* Account Settings - Locked */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onShowSignIn && onShowSignIn()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onShowSignIn && onShowSignIn(); } }}
                  className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 rounded-lg bg-[#F5F5F5]/50 opacity-60 cursor-pointer hover:opacity-90 active:scale-[0.99] transition">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 flex-shrink-0 bg-[#E8E0D8]/60 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#2A324B]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-medium text-[#2A324B]/60 flex items-center gap-2">
                        Account Settings
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div className="text-sm text-[#2A324B]/40">Manage your account preferences</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* How Son:mi works - always visible */}
              <button
                className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left"
                onClick={() => setActiveView('how-it-works')}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-[#2A324B]">How Son:mi works</div>
                    <div className="text-sm text-[#2A324B]/70">Limitations & important info</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Sign In / Create account */}
              <div className="mt-6">
                <button
                  onClick={() => {
                    onClose();
                    if (onShowSignIn) onShowSignIn();
                  }}
                  className="w-full py-3 bg-[#341C1C] text-white rounded-xl font-semibold text-sm hover:bg-[#5a3030] transition-colors"
                >
                  Create account
                </button>
                <button
                  onClick={() => {
                    onClose();
                    if (onSignInDirect) onSignInDirect();
                    else if (onShowSignIn) onShowSignIn();
                  }}
                  className="w-full py-2.5 mt-2 text-sm text-[#2A324B]/60 hover:text-[#2A324B] transition-colors"
                >
                  Sign in
                </button>
              </div>
            </div>
          );
        }

        // Full menu for logged-in users
        return (
          <div className="h-full overflow-y-auto px-4 pt-3 pb-4">
            <SectionHeader title="Menu" onBack={onClose} className="mb-2" />
            <div className="space-y-2">
              {SHOW_COLOUR_ANALYSIS && <ColourAnalysisMenuItem />}

              {/* 1. Analyse a Product */}
              <button
                className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left"
                onClick={() => setActiveView('catalog')}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-[#2A324B]">Analyse a Product</div>
                    <div className="text-sm text-[#2A324B]/70">See how any product matches your skin</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* 2. My Routine (feature-flagged) */}
              {process.env.REACT_APP_ENABLE_SKIN_PROGRESS === 'true' && (
              <button
                className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left"
                onClick={() => setActiveView('routine')}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#2A324B] flex items-center gap-2">
                      My Routine
                      <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full leading-none">Beta</span>
                    </div>
                    <div className="text-sm text-[#2A324B]/70">Build and track your skincare routine</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              )}

              {/* 3. Skin Journal (feature-flagged) */}
              {process.env.REACT_APP_ENABLE_SKIN_PROGRESS === 'true' && (
              <button
                className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left"
                onClick={() => setActiveView('progress')}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#2A324B] flex items-center gap-2">
                      Skin Journal
                      <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full leading-none">Beta</span>
                    </div>
                    <div className="text-sm text-[#2A324B]/70">Track your skin's progress over time</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              )}

              {/* 4. My Profile */}
              <button
                className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left"
                onClick={() => setActiveView('personal-profile')}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-[#2A324B]">My Profile</div>
                    <div className="text-sm text-[#2A324B]/70">Your skin type, tone & preferences</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* 5. My Products — saved & analysed products hub.
                  2026-06-09 (Sofia feedback #3): stale-while-revalidate
                  caching. Previously every tap re-fetched the whole list from
                  the backend (slow on cold-start), even if we already had it
                  cached in savedProducts. Now: if we have ANY cached
                  products, jump into the page immediately and silently refresh
                  in the background. Only block on the initial cold load. */}
              <button
                  className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left disabled:opacity-50"
                  onClick={async () => {
                    const hasCachedProducts = Array.isArray(savedProducts) && savedProducts.length > 0;
                    if (hasCachedProducts) {
                      // Cache hit — jump in, refresh quietly in the background
                      setActiveView('shelf');
                      if (onLoadProducts) {
                        Promise.resolve(onLoadProducts()).catch(() => {});
                      }
                      return;
                    }
                    // Cold cache — keep the existing loading flow
                    setIsLoadingProducts(true);
                    try {
                      if (onLoadProducts) await onLoadProducts();
                    } catch {}
                    finally {
                      setIsLoadingProducts(false);
                      setActiveView('shelf');
                    }
                  }}
                  disabled={isLoadingProducts}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                      {isLoadingProducts ? (
                        <svg className="animate-spin h-5 w-5 text-[#2A324B]" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-[#2A324B]">{isLoadingProducts ? 'Loading...' : 'My Products'}</div>
                      <div className="text-sm text-[#2A324B]/70">Your saved and analysed products</div>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

              {/* 6. Account Settings */}
              <button
                className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left"
                onClick={() => setActiveView('account-settings')}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-[#2A324B]">Account Settings</div>
                    <div className="text-sm text-[#2A324B]/70">Manage your account</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* 7. How it works */}
              <button
                className="w-full min-h-[64px] flex items-center justify-between gap-2 px-2 py-2.5 hover:bg-[#F3D5B5]/20 rounded-lg transition-colors text-left"
                onClick={() => setActiveView('how-it-works')}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 flex-shrink-0 bg-[#F3D5B5]/40 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-[#2A324B]">How Son:mi works</div>
                    <div className="text-sm text-[#2A324B]/70">Limitations & important info</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-[#2A324B]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

            </div>
          </div>
        );
    }
  };

  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />
      
      {/* Sheet */}
      {/* 2026-06-09 (Sofia feedback): visible page frame removed.
          - bg-[#FAFAF8] (gray) → bg-white
          - border-l hairline removed (was the dark left edge in screenshots)
          - max-w-md/max-w-lg kept — the desktop cap is fine; mobile is w-full */}
      <div className="fixed right-0 top-0 h-full w-full sm:max-w-md md:max-w-lg bg-white z-50 transform transition-transform duration-300 overflow-hidden">
        <div className="h-full">
          {renderContent()}
        </div>
      </div>

      {/* Create List Modal */}
      {showCreateListModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-[#2A324B] font-medium text-lg mb-4">Create New List</h3>
            <input
              type="text"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="Enter list name..."
              className="w-full px-3 py-2 border border-[#341C1C]/20 rounded-lg text-[#2A324B] text-sm focus:outline-none focus:ring-2 focus:ring-[#F3D5B5] focus:border-transparent mb-4"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCreateListModal(false);
                  setNewListName('');
                }}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-[#2A324B] py-2 px-4 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateList}
                disabled={!newListName.trim()}
                className="flex-1 bg-[#F3D5B5] hover:bg-[#F3D5B5]/80 disabled:bg-gray-100 disabled:text-gray-400 text-[#2A324B] py-2 px-4 rounded-lg text-sm font-medium transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add product to list modal */}
      {addToListCategory && addMode === 'choose' && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-[60] p-4" onClick={closeAddToList}>
          <div className="bg-white rounded-2xl w-full max-w-sm pb-6 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 pt-5 pb-4 border-b border-[#F0F0F0]">
              <h3 className="font-bold text-[#2A324B] text-base">Add a product</h3>
              <p className="text-xs text-[#2A324B]/45 mt-0.5">How would you like to add it?</p>
            </div>
            <div className="px-4 pt-4 space-y-2">
              <button
                onClick={() => setAddMode('manual')}
                className="w-full flex items-center gap-3 p-3.5 bg-white hover:bg-[#F3D5B5]/20 rounded-2xl transition-colors text-left"
              >
                <div className="w-9 h-9 rounded-xl bg-[#F3D5B5]/40 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4.5 h-4.5 w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#2A324B]">Add manually</p>
                  <p className="text-xs text-[#2A324B]/45 mt-0.5">Type in product name and brand</p>
                </div>
              </button>
              <button
                onClick={() => {
                  // Carry the target list through to ProductAnalysisView so
                  // when the user hits "Save to Catalogue" we skip the picker
                  // and save straight to the list they came from.
                  setAnalyseTargetList(addToListCategory);
                  closeAddToList();
                  setActiveView('product-analysis');
                }}
                className="w-full flex items-center gap-3 p-3.5 bg-white hover:bg-[#F3D5B5]/20 rounded-2xl transition-colors text-left"
              >
                <div className="w-9 h-9 rounded-xl bg-[#F3D5B5]/40 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-[#2A324B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#2A324B]">Take a picture</p>
                  <p className="text-xs text-[#2A324B]/45 mt-0.5">Snap the product and we'll add it to this list</p>
                </div>
              </button>
            </div>
            <div className="px-4 mt-4">
              <button onClick={closeAddToList} className="w-full py-2.5 text-sm text-[#2A324B]/50 font-medium hover:text-[#2A324B] transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Manual add form */}
      {addToListCategory && addMode === 'manual' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={closeAddToList}>
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 pt-5 pb-4 border-b border-[#F0F0F0]">
              <h3 className="font-bold text-[#2A324B] text-base">Add product</h3>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="text-[13px] font-semibold uppercase tracking-wider text-[#2A324B]/40 block mb-1">Product name *</label>
                <input
                  value={manualName}
                  onChange={e => setManualName(e.target.value)}
                  placeholder="e.g. Vitamin C Serum"
                  className="w-full text-sm text-[#2A324B] bg-[#F5F5F5] rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#F3D5B5]/60"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[13px] font-semibold uppercase tracking-wider text-[#2A324B]/40 block mb-1">Brand</label>
                <input
                  value={manualBrand}
                  onChange={e => setManualBrand(e.target.value)}
                  placeholder="e.g. The Ordinary"
                  className="w-full text-sm text-[#2A324B] bg-[#F5F5F5] rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#F3D5B5]/60"
                />
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-3">
              <button onClick={() => setAddMode('choose')} className="flex-1 py-2.5 text-sm font-medium text-[#2A324B]/60 bg-[#F5F5F5] hover:bg-[#EDEDED] rounded-xl transition-colors">Back</button>
              <button
                onClick={submitManualAdd}
                disabled={!manualName.trim()}
                className="flex-1 py-2.5 text-sm font-semibold text-[#2A324B] bg-[#F3D5B5] hover:bg-[#EFC9A0] disabled:bg-[#F5F5F5] disabled:text-[#2A324B]/30 rounded-xl transition-colors"
              >
                Add product
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default MenuSheet;
